import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/lib/auth";
import { StatusStepper, StatusChip, STATUS_STEPS } from "@/components/site/CaseStatus";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    ArrowLeft,
    Send,
    FileText,
    MessageSquare,
    Receipt,
    CheckCircle2,
    CreditCard,
    Upload,
    Download,
    Paperclip,
    Trash2,
    Loader2,
} from "lucide-react";

export default function CaseDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [caseData, setCaseData] = useState(null);
    const [updates, setUpdates] = useState([]);
    const [messages, setMessages] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [newUpdate, setNewUpdate] = useState("");
    const [newMessage, setNewMessage] = useState("");
    const [newInvoice, setNewInvoice] = useState({ description: "", amount_sek: "" });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [payingId, setPayingId] = useState(null);
    const fileInputRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const load = useCallback(async () => {
        try {
            const [c, u, m, inv, docs] = await Promise.all([
                axios.get(`${API}/cases/${id}`, { withCredentials: true }),
                axios.get(`${API}/cases/${id}/updates`, { withCredentials: true }),
                axios.get(`${API}/cases/${id}/messages`, { withCredentials: true }),
                axios.get(`${API}/cases/${id}/invoices`, { withCredentials: true }),
                axios.get(`${API}/cases/${id}/documents`, { withCredentials: true }),
            ]);
            setCaseData(c.data);
            setUpdates(u.data.items || []);
            setMessages(m.data.items || []);
            setInvoices(inv.data.items || []);
            setDocuments(docs.data.items || []);
        } catch {
            toast.error("Kunde inte hämta ärendet.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    // Return from Stripe Checkout
    useEffect(() => {
        const payment = searchParams.get("payment");
        const sessionId = searchParams.get("session_id");
        if (payment === "success" && sessionId) {
            (async () => {
                try {
                    await axios.get(
                        `${API}/stripe/checkout-status/${sessionId}`,
                        { withCredentials: true },
                    );
                    toast.success("Tack! Betalningen är registrerad.");
                } catch {
                    toast.message("Vi bekräftar din betalning inom kort.");
                } finally {
                    searchParams.delete("payment");
                    searchParams.delete("session_id");
                    setSearchParams(searchParams, { replace: true });
                    load();
                }
            })();
        } else if (payment === "cancelled") {
            toast.message("Betalningen avbröts.");
            searchParams.delete("payment");
            setSearchParams(searchParams, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startCheckout = async (invoice) => {
        setPayingId(invoice.id);
        try {
            const { data } = await axios.post(
                `${API}/cases/${id}/invoices/${invoice.id}/checkout`,
                { origin: window.location.origin },
                { withCredentials: true },
            );
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("no url");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Kunde inte starta betalningen.",
            );
            setPayingId(null);
        }
    };

    const uploadDoc = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await axios.post(`${API}/cases/${id}/documents`, fd, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Dokumentet uppladdat.");
            load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Kunde inte ladda upp dokumentet.",
            );
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const downloadDoc = async (doc) => {
        try {
            const res = await axios.get(
                `${API}/cases/${id}/documents/${doc.id}/download`,
                { withCredentials: true, responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Kunde inte ladda ner filen.");
        }
    };

    const deleteDoc = async (doc) => {
        if (!window.confirm(`Ta bort "${doc.filename}"?`)) return;
        try {
            await axios.delete(
                `${API}/cases/${id}/documents/${doc.id}`,
                { withCredentials: true },
            );
            toast.success("Dokumentet borttaget.");
            load();
        } catch {
            toast.error("Kunde inte ta bort dokumentet.");
        }
    };

    const postUpdate = async () => {
        if (!newUpdate.trim()) return;
        try {
            await axios.post(
                `${API}/cases/${id}/updates`,
                { body: newUpdate },
                { withCredentials: true },
            );
            setNewUpdate("");
            toast.success("Uppdateringen publicerad.");
            load();
        } catch {
            toast.error("Kunde inte publicera uppdatering.");
        }
    };

    const postMessage = async () => {
        if (!newMessage.trim()) return;
        try {
            await axios.post(
                `${API}/cases/${id}/messages`,
                { body: newMessage },
                { withCredentials: true },
            );
            setNewMessage("");
            load();
        } catch {
            toast.error("Kunde inte skicka meddelandet.");
        }
    };

    const changeStatus = async (status) => {
        try {
            await axios.patch(
                `${API}/cases/${id}`,
                { status },
                { withCredentials: true },
            );
            toast.success(`Status uppdaterad: ${status}`);
            load();
        } catch {
            toast.error("Kunde inte uppdatera status.");
        }
    };

    const createInvoice = async () => {
        if (!newInvoice.description.trim() || !newInvoice.amount_sek) return;
        try {
            await axios.post(
                `${API}/cases/${id}/invoices`,
                {
                    description: newInvoice.description,
                    amount_sek: parseFloat(newInvoice.amount_sek),
                },
                { withCredentials: true },
            );
            setNewInvoice({ description: "", amount_sek: "" });
            toast.success("Faktura skapad.");
            load();
        } catch {
            toast.error("Kunde inte skapa faktura.");
        }
    };

    const togglePaid = async (inv) => {
        try {
            await axios.patch(
                `${API}/cases/${id}/invoices/${inv.id}?paid=${!inv.paid}`,
                {},
                { withCredentials: true },
            );
            load();
        } catch {
            toast.error("Kunde inte uppdatera fakturan.");
        }
    };

    if (loading) return <div className="py-24 text-center text-[#A1A1AA]">Läser in...</div>;
    if (!caseData) return <div className="py-24 text-center">Ärendet hittades inte.</div>;

    const backHref = isAdmin ? "/admin" : "/portal";

    return (
        <section data-testid="page-case-detail" className="py-14 min-h-[70vh]">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <Link
                    to={backHref}
                    className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C4A977] transition-colors"
                >
                    <ArrowLeft size={14} /> Tillbaka
                </Link>

                <div className="mt-8 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div>
                        <p className="overline">{caseData.area}</p>
                        <h1 className="mt-3 font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                            {caseData.title}
                        </h1>
                        {caseData.description && (
                            <p className="mt-4 text-[#A1A1AA] max-w-2xl leading-relaxed">
                                {caseData.description}
                            </p>
                        )}
                    </div>
                    <StatusChip status={caseData.status} />
                </div>

                <div className="mt-8 bg-[#111113] border border-white/5 p-6 rounded-sm overflow-x-auto">
                    <StatusStepper status={caseData.status} />
                </div>

                {isAdmin && (
                    <div className="mt-6 bg-[#111113] border border-[#C4A977]/20 p-6 rounded-sm">
                        <p className="overline mb-3">Admin-verktyg</p>
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-xs text-[#A1A1AA]">Byt status:</span>
                            <Select onValueChange={changeStatus} value={caseData.status}>
                                <SelectTrigger
                                    data-testid="admin-status-select"
                                    className="bg-[#0A0A0B] border-white/10 w-[200px] rounded-sm"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#111113] border-white/10 text-[#F4F4F5]">
                                    {STATUS_STEPS.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <span className="text-xs text-[#A1A1AA]">
                                Klient: {caseData.client_email}{" "}
                                {caseData.client_user_id ? "(kopplad)" : "(ej kopplad – väntar på inloggning)"}
                            </span>
                        </div>
                    </div>
                )}

                {/* Updates */}
                <div className="mt-12">
                    <h2 className="font-serif text-2xl flex items-center gap-3">
                        <FileText size={18} className="text-[#C4A977]" /> Uppdateringar
                    </h2>
                    {isAdmin && (
                        <div className="mt-4 bg-[#111113] border border-white/5 p-5 rounded-sm">
                            <Textarea
                                data-testid="admin-update-input"
                                value={newUpdate}
                                onChange={(e) => setNewUpdate(e.target.value)}
                                placeholder="Skriv en uppdatering till klienten..."
                                rows={3}
                                className="bg-[#0A0A0B] border-white/10 rounded-sm"
                            />
                            <Button
                                data-testid="admin-update-submit"
                                onClick={postUpdate}
                                className="mt-3 bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                            >
                                Publicera uppdatering
                            </Button>
                        </div>
                    )}
                    <div className="mt-5 space-y-4">
                        {updates.length === 0 && (
                            <p className="text-sm text-[#A1A1AA]">Inga uppdateringar ännu.</p>
                        )}
                        {updates.map((u) => (
                            <div
                                key={u.id}
                                data-testid={`update-${u.id}`}
                                className="bg-[#111113] border-l-2 border-[#C4A977] p-5 rounded-sm"
                            >
                                <p className="whitespace-pre-wrap text-[#F4F4F5]/90">
                                    {u.body}
                                </p>
                                <p className="mt-3 text-xs text-[#A1A1AA]">
                                    {u.author_name} ·{" "}
                                    {new Date(u.created_at).toLocaleString("sv-SE")}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Messages */}
                <div className="mt-14">
                    <h2 className="font-serif text-2xl flex items-center gap-3">
                        <MessageSquare size={18} className="text-[#C4A977]" /> Meddelanden
                    </h2>
                    <div className="mt-4 bg-[#111113] border border-white/5 rounded-sm p-5 space-y-3 max-h-[500px] overflow-y-auto">
                        {messages.length === 0 && (
                            <p className="text-sm text-[#A1A1AA]">Inga meddelanden ännu.</p>
                        )}
                        {messages.map((m) => {
                            const mine = m.author_id === user?.user_id;
                            return (
                                <div
                                    key={m.id}
                                    data-testid={`message-${m.id}`}
                                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[80%] px-4 py-3 rounded-sm border ${
                                            mine
                                                ? "bg-[#C4A977]/10 border-[#C4A977]/30"
                                                : "bg-[#0A0A0B] border-white/10"
                                        }`}
                                    >
                                        <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                                        <p className="mt-2 text-[10px] text-[#A1A1AA] tracking-wide">
                                            {m.author_name} ·{" "}
                                            {new Date(m.created_at).toLocaleString("sv-SE")}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-4 flex gap-3">
                        <Textarea
                            data-testid="message-input"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Skriv ett meddelande..."
                            rows={2}
                            className="bg-[#0A0A0B] border-white/10 rounded-sm"
                        />
                        <Button
                            data-testid="message-send"
                            onClick={postMessage}
                            className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm shrink-0"
                        >
                            <Send size={14} className="mr-2" /> Skicka
                        </Button>
                    </div>
                </div>

                {/* Documents */}
                <div className="mt-14">
                    <h2 className="font-serif text-2xl flex items-center gap-3">
                        <Paperclip size={18} className="text-[#C4A977]" /> Dokument
                    </h2>
                    <div className="mt-4 bg-[#111113] border border-white/5 p-5 rounded-sm">
                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                ref={fileInputRef}
                                data-testid="doc-file-input"
                                type="file"
                                className="hidden"
                                onChange={(e) => uploadDoc(e.target.files?.[0])}
                            />
                            <Button
                                data-testid="doc-upload-btn"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                            >
                                {uploading ? (
                                    <><Loader2 size={14} className="mr-2 animate-spin" /> Laddar upp...</>
                                ) : (
                                    <><Upload size={14} className="mr-2" /> Ladda upp dokument</>
                                )}
                            </Button>
                            <p className="text-xs text-[#A1A1AA]">
                                Max {20} MB. Tillåtna format: PDF, Word, Excel, bilder, TXT, ZIP.
                            </p>
                        </div>
                    </div>
                    <div className="mt-5 space-y-3">
                        {documents.length === 0 && (
                            <p className="text-sm text-[#A1A1AA]">Inga dokument ännu.</p>
                        )}
                        {documents.map((d) => (
                            <div
                                key={d.id}
                                data-testid={`doc-${d.id}`}
                                className="flex items-center justify-between bg-[#111113] border border-white/5 p-4 rounded-sm gap-4"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <FileText size={16} className="text-[#C4A977] shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm truncate text-[#F4F4F5]">{d.filename}</p>
                                        <p className="text-xs text-[#A1A1AA]">
                                            {(d.size_bytes / 1024).toFixed(1)} KB ·{" "}
                                            Uppladdad av {d.uploaded_by_name} ·{" "}
                                            {new Date(d.uploaded_at).toLocaleDateString("sv-SE")}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        data-testid={`doc-download-${d.id}`}
                                        onClick={() => downloadDoc(d)}
                                        className="border-white/10 rounded-sm text-xs"
                                    >
                                        <Download size={12} className="mr-1" /> Ladda ner
                                    </Button>
                                    {isAdmin && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            data-testid={`doc-delete-${d.id}`}
                                            onClick={() => deleteDoc(d)}
                                            className="text-[#A1A1AA] hover:text-red-300 rounded-sm text-xs"
                                        >
                                            <Trash2 size={12} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Invoices */}
                <div className="mt-14">
                    <h2 className="font-serif text-2xl flex items-center gap-3">
                        <Receipt size={18} className="text-[#C4A977]" /> Fakturor
                    </h2>
                    {isAdmin && (
                        <div className="mt-4 bg-[#111113] border border-white/5 p-5 rounded-sm grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3">
                            <input
                                data-testid="invoice-desc"
                                value={newInvoice.description}
                                onChange={(e) =>
                                    setNewInvoice((v) => ({ ...v, description: e.target.value }))
                                }
                                placeholder="Beskrivning"
                                className="bg-[#0A0A0B] border border-white/10 rounded-sm px-3 py-2 text-sm"
                            />
                            <input
                                data-testid="invoice-amount"
                                value={newInvoice.amount_sek}
                                onChange={(e) =>
                                    setNewInvoice((v) => ({ ...v, amount_sek: e.target.value }))
                                }
                                placeholder="Belopp (SEK)"
                                type="number"
                                className="bg-[#0A0A0B] border border-white/10 rounded-sm px-3 py-2 text-sm"
                            />
                            <Button
                                data-testid="invoice-create"
                                onClick={createInvoice}
                                className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                            >
                                Skapa faktura
                            </Button>
                        </div>
                    )}
                    <div className="mt-5 space-y-3">
                        {invoices.length === 0 && (
                            <p className="text-sm text-[#A1A1AA]">Inga fakturor ännu.</p>
                        )}
                        {invoices.map((inv) => (
                            <div
                                key={inv.id}
                                data-testid={`invoice-${inv.id}`}
                                className="flex items-center justify-between bg-[#111113] border border-white/5 p-5 rounded-sm"
                            >
                                <div>
                                    <p className="text-[#F4F4F5]">{inv.description}</p>
                                    <p className="mt-1 text-xs text-[#A1A1AA]">
                                        {new Date(inv.created_at).toLocaleDateString("sv-SE")}
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 flex-wrap justify-end">
                                    <p className="font-serif text-xl">
                                        {inv.amount_sek.toLocaleString("sv-SE")} SEK
                                    </p>
                                    {inv.paid ? (
                                        <span className="inline-flex items-center gap-2 text-emerald-400 text-xs">
                                            <CheckCircle2 size={14} /> Betald
                                        </span>
                                    ) : (
                                        <span className="text-amber-300 text-xs">Obetald</span>
                                    )}
                                    {!inv.paid && !isAdmin && (
                                        <Button
                                            size="sm"
                                            data-testid={`invoice-pay-${inv.id}`}
                                            onClick={() => startCheckout(inv)}
                                            disabled={payingId === inv.id}
                                            className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm text-xs"
                                        >
                                            {payingId === inv.id ? (
                                                <><Loader2 size={12} className="mr-1 animate-spin" /> Laddar...</>
                                            ) : (
                                                <><CreditCard size={12} className="mr-1" /> Betala faktura</>
                                            )}
                                        </Button>
                                    )}
                                    {isAdmin && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => togglePaid(inv)}
                                            className="border-white/10 text-xs rounded-sm"
                                        >
                                            Markera {inv.paid ? "obetald" : "betald"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
